import {
  Box,
  Button,
  FormLabel,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Stack,
  FormControl,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Descendant, Element } from "slate";
import CommunicationTemplatesStyles from "./CommunicationTemplatesStyles";
import RichTextEditor from "../components/RichTextEditor/RichTextEditor";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  createCommunicationTemplates,
  updateCommunicationTemplates,
} from "./CommunicationTemplatesActions";
import Switch from "@mui/material/Switch";
import {
  ICreateCommunicationTemplatesPayload,
  IUpdateCommunicationTemplatesPayload,
} from "../../models/admin/communicationTemplates/communicatinTemplates";
import {
  IGetOptionsResponse,
  IOption,
} from "../../models/user/DynamicForm/DynamicForm";
import { CustomAction } from "../../store/customAction";
import { API_GET_LOOKUP_VALUES } from "../../Apis/ApiEndPoints";
import { Api } from "../..";
import {
  CustomElement,
  CustomText,
} from "../components/RichTextEditor/RichTextEditor.types";
import { ICreatedTemplateData } from "../../models/admin/communicationTemplates/communicatinTemplates";
import { slateToMarkdown } from "../../common/CMS/SlateToMarkdown/SlateToMarkdown";
import { markdownToSlate } from "../../common/CMS/MarkdownToSlate/MarkdownToSlate";
import { RootState } from "../../store/store";
import MessageDialog from "../../components/MessageDialog/MessageDialog";

interface ICreateCommunicationTemplateProps {
  isEdit: boolean;
  template?: ICreatedTemplateData | null;
  handleDrawerClose: () => void;
}
interface ICreateTemplateFormValues {
  templateName: string;
  subjectLine: string;
  emailBody: Descendant[];
  placeholders: string[];
  includeAttachments: boolean;
}

// Add this interface for errors
interface IFormErrors {
  templateName: string;
  subjectLine: string;
  emailBody: string;
}

const isSlateContentEmpty = (nodes: Descendant[]): boolean => {
  for (const node of nodes) {
    if ("text" in node) {
      if (node.text.trim() !== "") return false;
    } else if ("children" in node && Array.isArray(node.children)) {
      if (!isSlateContentEmpty(node.children)) return false;
    }
  }

  return true;
};

const CreateCommunicationTemplate: FC<ICreateCommunicationTemplateProps> = ({
  isEdit,
  template,
  handleDrawerClose,
}) => {
  const dispatch = useAppDispatch();
  const { calledReducerType, isSuccess, isError } = useAppSelector(
    (state: RootState) => state.common
  );
  const { allCommunicationTemplates } = useAppSelector(
    (state: RootState) => state.communicationTemplates
  );
  const [predefinedPlaceholders, setPredefinedPlaceholders] = useState<
    IOption[]
  >([]);
  const [formValues, setFormValues] = useState<ICreateTemplateFormValues>({
    templateName: "",
    subjectLine: "",
    emailBody: [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ],
    placeholders: [],
    includeAttachments: false,
  });

  const [newPlaceholder, setNewPlaceholder] = useState<string>("");
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const [errors, setErrors] = useState<IFormErrors>({
    templateName: "",
    subjectLine: "",
    emailBody: "",
  });

  const originalFormValuesRef = useRef<ICreateTemplateFormValues | null>(null);

  const validateTemplateName = (name: string) => {
    if (!name.trim()) {
      return "Template name is required";
    }
    const isDuplicate = allCommunicationTemplates.some(
      (existingTemplate) =>
        existingTemplate.templateName.toLowerCase() === name.toLowerCase() &&
        (!isEdit || (isEdit && existingTemplate.id !== template?.id))
    );
    if (isDuplicate) {
      return "A template with this name already exists";
    }
    return "";
  };

  const scrollToError = (fieldName: keyof IFormErrors) => {
    const errorElement = document.getElementById(`${fieldName}-error`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Add validation function
  const validateForm = () => {
    const newErrors = { ...errors };
    let isValid = true;

    // Template Name validation
    const templateNameError = validateTemplateName(formValues.templateName);
    if (templateNameError) {
      newErrors.templateName = templateNameError;
      isValid = false;
    }

    // Subject Line validation
    if (!formValues.subjectLine.trim()) {
      newErrors.subjectLine = "Subject line is required";
      isValid = false;
    }

    // Email Body validation
    if (
      !formValues.emailBody ||
      formValues.emailBody.length === 0 ||
      isSlateContentEmpty(formValues.emailBody)
    ) {
      newErrors.emailBody = "Email body content is required";
      isValid = false;
    }

    setErrors(newErrors);
    
    if (!isValid) {
      const firstErrorField = Object.keys(newErrors).find(
        (key) => newErrors[key as keyof IFormErrors]
      ) as keyof IFormErrors;
      if (firstErrorField) {
        scrollToError(firstErrorField);
      }
    }
    
    return isValid;
  };

  const handleCreateOrUpdateTemplate = () => {
    if (!validateForm()) {
      return;
    }
    const markdownContent = slateToMarkdown(formValues.emailBody);

    if (isEdit && template) {
      const { emailBody, includeAttachments, ...withoutRichText } = formValues;
      const currentTime = new Date().toString();
      const requestPayload: IUpdateCommunicationTemplatesPayload = {
        ...withoutRichText,
        id: template.id,
        bodyContent: markdownContent,
        attachmentOption: includeAttachments || false,
        updatedAt: currentTime,
      };
      dispatch(updateCommunicationTemplates(requestPayload));
    } else {
      const { emailBody, includeAttachments, ...withoutRichText } = formValues;
      const requestPayload: ICreateCommunicationTemplatesPayload = {
        ...withoutRichText,
        bodyContent: markdownContent,
        attachmentOption: includeAttachments || false,
      };
      dispatch(createCommunicationTemplates(requestPayload));
    }
  };

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    handleDrawerClose();
  };

  useEffect(() => {
    const fetchOptions = async () => {
      const fetchedOptions = await fetchPlaceholderOptions();
      setPredefinedPlaceholders(fetchedOptions || []);
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (isEdit && template) {
      const initialValues = {
        templateName: template.templateName,
        subjectLine: template.subjectLine,
        emailBody: markdownToSlate(template.bodyContent),
        placeholders: template.placeholders,
        includeAttachments: template.attachmentOption,
      };
      setFormValues(initialValues);
      originalFormValuesRef.current = initialValues;
    }
  }, [isEdit, template]);
  const isFormChanged =
    JSON.stringify(formValues) !==
    JSON.stringify(originalFormValuesRef.current);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "update/communicationTemplates" ||
        calledReducerType === "create/communicationTemplates" ||
        calledReducerType === "delete/communicationTemplates")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  const fetchPlaceholderOptions = useCallback(async () => {
    try {
      const response: IGetOptionsResponse = await CustomAction(
        Api.get(`${API_GET_LOOKUP_VALUES}/emailTemplatePlaceholders`),
        dispatch,
        "fetchPlaceholderOptions"
      );
      return response.data;
    } catch (error) {
      return [];
    }
  }, []);

  const handlePlaceholderChange = (event: any) => {
    const value = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      placeholders: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleAddPlaceholder = () => {
    if (newPlaceholder.trim()) {
      // Remove any existing curly braces first
      const cleanPlaceholder = newPlaceholder.trim().replace(/[{}]/g, "");

      setFormValues((prev) => ({
        ...prev,
        placeholders: [...prev.placeholders, cleanPlaceholder],
      }));
      setNewPlaceholder("");
    }
  };

  return (
    <Box>
      <Typography sx={{ marginBottom: "0.5rem" }}>
        {isEdit
          ? "Update the details of your communication template"
          : "Define a new communication template for your system"}
      </Typography>
      <Box sx={CommunicationTemplatesStyles.fieldBoxContainer}>
        <TextField
          id="templateName-error"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Template Name
              <Typography component="span" sx={{ ml: 0.2 }}>
                *
              </Typography>
              <Tooltip title="A unique identifier for this template">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
          value={formValues?.templateName}
          onChange={(e) => {
            const newName = e.target.value;
            setFormValues({
              ...formValues,
              templateName: newName,
            });
            // Real-time validation
            const error = validateTemplateName(newName);
            setErrors({ ...errors, templateName: error });
          }}
          fullWidth
          error={!!errors.templateName}
          helperText={errors.templateName}
          sx={{ 
            marginTop: "0.2rem",
            "& .MuiFormHelperText-root": {
              color: "error.main",
              fontWeight: 500,
              fontSize: "0.875rem",
              marginTop: "4px"
            }
          }}
        />
      </Box>
      <Box sx={CommunicationTemplatesStyles.fieldBoxContainer}>
        <TextField
          id="subjectLine-error"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Email Subject Line
              <Typography component="span" sx={{ ml: 0.2 }}>
                *
              </Typography>
              <Tooltip title="The subject line that will appear in the email">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
          value={formValues?.subjectLine}
          onChange={(e) => {
            setFormValues({
              ...formValues,
              subjectLine: e.target.value,
            });
            setErrors({ ...errors, subjectLine: "" });
          }}
          fullWidth
          error={!!errors.subjectLine}
          helperText={errors.subjectLine}
          sx={{ 
            marginTop: "0.2rem",
            "& .MuiFormHelperText-root": {
              color: "error.main",
              fontWeight: 500,
              fontSize: "0.875rem",
              marginTop: "4px"
            }
          }}
        />
      </Box>
      <Box sx={CommunicationTemplatesStyles.fieldBoxContainer}>
        <RichTextEditor
          value={formValues?.emailBody}
          fontSize="16px"
          onChange={(value) => {
            setFormValues({
              ...formValues,
              emailBody: value,
            });
            setErrors({ ...errors, emailBody: "" });
          }}
          isError={!!errors.emailBody}
          label="Email Body Content"
          required={true}
          tooltipText="Format your email using the toolbar at the top"
        />
        {errors.emailBody && (
          <Typography
            id="emailBody-error-text"
            color="error"
            sx={{ 
              fontSize: "0.875rem", 
              marginTop: "4px",
              fontWeight: 500
            }}
          >
            {errors.emailBody}
          </Typography>
        )}
      </Box>
      <Box sx={CommunicationTemplatesStyles.fieldBoxContainer}>
        <FormLabel sx={CommunicationTemplatesStyles.formLabel}>
          Placeholder Variables
          <Tooltip
            title="Select from predefined placeholders or add custom ones"
            PopperProps={{
              sx: CommunicationTemplatesStyles?.toolTipPopper,
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        </FormLabel>
        <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
          <FormControl fullWidth>
            <Select
              multiple
              value={formValues.placeholders}
              onChange={handlePlaceholderChange}
              displayEmpty
              renderValue={() => "Select predefined placeholders"}
              MenuProps={{
                PaperProps: {
                  sx: {
                    zIndex: 1400,
                  },
                },
                sx: {
                  zIndex: 1400,
                  height: "300px",
                },
              }}
            >
              {predefinedPlaceholders?.map((placeholder) => (
                <MenuItem key={placeholder?.code} value={placeholder?.code}>
                  {`{{${placeholder?.label}}}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={CommunicationTemplatesStyles.attachmentBoxContainer}>
            <TextField
              fullWidth
              value={newPlaceholder}
              onChange={(e) => setNewPlaceholder(e.target.value)}
              placeholder="Enter custom placeholder"
            />
            <Button
              variant="contained"
              onClick={handleAddPlaceholder}
              startIcon={<AddIcon />}
              sx={{ borderRadius: "4px", height: "fit-content" }}
            >
              Add
            </Button>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ marginTop: 1, gap: 1 }}
        >
          {formValues.placeholders.map((value) => (
            <Chip
              key={value}
              label={value.includes("{{") ? value : `{{${value}}}`}
              onDelete={() => {
                setFormValues((prev) => ({
                  ...prev,
                  placeholders: prev.placeholders.filter(
                    (item) => item !== value
                  ),
                }));
              }}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={CommunicationTemplatesStyles.attachmentBoxContainer}>
        <FormLabel sx={CommunicationTemplatesStyles.formLabel}>
          Include Attachments
          <Tooltip
            title="Include attachment options for this template?"
            PopperProps={{
              sx: CommunicationTemplatesStyles?.toolTipPopper,
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        </FormLabel>
        <Switch
          inputProps={{ "aria-label": "Include Attachments" }}
          checked={formValues?.includeAttachments}
          value={formValues?.includeAttachments}
          onChange={(e) =>
            setFormValues({
              ...formValues,
              includeAttachments: e.target.checked,
            })
          }
        />
      </Box>
      <Box sx={CommunicationTemplatesStyles.actionButtonContainer}>
        <Button
          variant="contained"
          onClick={handleCreateOrUpdateTemplate}
          sx={{ borderRadius: "4px" }}
          disabled={isEdit ? !isFormChanged : false}
        >
          {isEdit ? "Update Template" : "Create Template"}
        </Button>
      </Box>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </Box>
  );
};

export default CreateCommunicationTemplate;
